/* ------------------------------------*\
    #VIDEO PLAYER SCRIPT
\*------------------------------------ */

import Plyr from 'plyr/dist/plyr';
import Component from '../../../../../src/js/module/component';
import logger from '../../../../../src/js/module/logger';
import libs from '../../../../../src/js/module/libs';

const EffectiveConnectionTypeEnum = Object.freeze({
    'slow-2g': 0,
    '2g': 1,
    '3g': 2,
    '4g': 3,
});

export default class VideoPlayer extends Component {

    static componentName = 'video';

    static componentSelector = '.js-video';

    static defaultOptions = {
        debug: false,
        autoplay: false,
        invertTime: false,
        controls: [
            'play',
            'play-large',
            'progress',
            'current-time',
            'mute',
            'volume',
            'captions',
            'fullscreen',
        ],
        fullscreen: {
            fallback: true,
        },
        youtube: {
            noCookie: true,
        },
        urls: {
            youtube: {
                api: '', // Disable YouTube API (for title/aspect ratio calculation) calls through third-party noembed.com service.
            },
        },
        autoplayMinimumConnection: null,
        initOnLoad: true,
    };

    constructor() {
        super(...arguments);
        this.initialized = false;
        /**
         * Cache the play/pause button.
         * @type {Element}
         */
        this.videoControl = document.querySelector('.js-background-video-control');

        /**
         * Track if the video was stopped via a user interaction or via connectivity/visibility APIs.
         * @type {boolean}
         */
        this.userForcedPause = false;

        /**
         * Track if the video had already been played.
         * @type {boolean}
         */
        this.didPlay = false;

        this.element.classList.add('c-video--unitialized');

        /**
         * @TODO: Call init() only when a consent was given …
         */
        if (this.options.initOnLoad) {
            this.init();
        }
    }

    init() {
        /**
         * Check if {@link Plyr} lib is present and only proceed if so.
         */
        if (Plyr) {
            /**
             * If the `autoplayMinimumConnection` option is set, we disable autoplay until we checked the connectivity.
             */
            if (this.options.autoplayMinimumConnection) {
                this.options.autoplay = false;
                this.element.removeAttribute('autoplay');
            }

            /**
             * If autoplay was set directly on the video and not via the options, we update it.
             */
            if (this.element.autoplay && !this.options.autoplayMinimumConnection) {
                this.options.autoplay = this.element.autoplay;
            }

            if (this.options.muted == null) {
                this.options.muted = this.element.muted;
                if (this.options.muted) {
                    /**
                     * Make plyr respect `[muted]`.
                     * @see https://github.com/sampotts/plyr/issues/1495#issuecomment-659735226
                     * @type {{enabled: boolean}}
                     */
                    this.options.storage = { enabled: false };
                    this.options.volume = 0;
                }
            }

            /**
             * Create a new {@link Plyr} instance.
             */
            this.plyr = new Plyr(this.element, this.options);

            /**
             * Get the current network information – only supported via Chromium for now.
             * @type {NetworkInformation|*}
             */
            this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

            /**
             * Get the `prefers-reduced-motion` media query setting.
             * @type {MediaQueryList}
             */
            const reduceMotionMediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
            this.prefersReducedMotion = reduceMotionMediaQueryList?.matches;

            /**
             * Bail out if users prefer reduced motion.
             */
            if (this.prefersReducedMotion) {
                this.pause();
            }

            /**
             * Add a listener for {@link MediaQueryListEvent} to handle media reduced motion media changes.
             */
            reduceMotionMediaQueryList.addEventListener('change', () => {
                if (reduceMotionMediaQueryList?.matches) {
                    this.stop();
                }
            });

            /**
             * Check the page visibility and stop/resume the video on 'visibilitychange' event.
             */
            document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

            /**
             * Check if the video is in a Hero Teaser and prevent video controls.
             * @TODO: This should be refactored so that the Video class has no connection to Hero Teaser.
             * Ideally the background video controls should establish a connection via `[aria-controls]`.
             */
            if (this.element?.closest('.c-hero-teaser__background-video')) {
                /**
                 * Handle play/pause for background video.
                 */
                this.videoControl?.addEventListener('click', () => {
                    this.toggleVideo(this.paused);
                    this.userForcedPause = this.paused;
                });
            }

            /**
             * Listen for play/pause for on the video itself and set the {@link paused} state.
             */
            this.element?.addEventListener('click', () => {
                this.userForcedPause = !this.paused;
            });

            /**
             * Need to register a separate handler for background videos because
             * {@link Plyr} stops events on mobile devices.
             * @see https://github.com/sampotts/plyr/issues/968
             * @see https://github.com/sampotts/plyr/issues/946#issuecomment-1104978722
             */
            if (this.videoControl && window.matchMedia('(pointer: coarse)').matches) {
                /**
                 * Handle play/pause for background images.
                 */
                this.element?.addEventListener('click', () => {
                    this.toggleVideo(this.paused);
                    this.userForcedPause = this.paused;
                });
            }

            /**
             * Check if the Network Information API is supported.
             * Otherwise, enable the video regardless.
             */
            if (this.connection) {
                this.sufficientConnection = this.checkConnectivity();
            }

            /**
             * Adjust background video controls.
             */
            this.plyr.on('play', () => {
                this.didPlay = true;
                /**
                 * Update background video controls only if it is contained in a Hero Teaser Background.
                 * @TODO: This should be refactored so that the Video class has no connection to Hero Teaser.
                 * Ideally the background video controls should establish a connection via `[aria-controls]`.
                 */
                if (this.element?.closest('.c-hero-teaser__background-video')) {
                    this.videoControl?.classList.remove('is-paused');
                }
            });

            /**
             * Adjust background video controls.
             */
            this.plyr.on('pause', () => {
                /**
                 * Update background video controls only if it is contained in a Hero Teaser Background.
                 * @TODO: This should be refactored so that the Video class has no connection to Hero Teaser.
                 * Ideally the background video controls should establish a connection via `[aria-controls]`.
                 */
                if (this.element?.closest('.c-hero-teaser__background-video')) {
                    this.videoControl?.classList.add('is-paused');
                }
            });

            /**
             * Check if the {@link this.options.autoplayMinimumConnection} option was set and if the user has not opted in for reduced motion.
             */
            if (this.options.autoplayMinimumConnection && !this.prefersReducedMotion) {
                /**
                 * Check if the current connection is sufficient and play the video.
                 * Otherwise, check if an autoplay was preferred and play it.
                 */
                this.element.preload = 'auto';
                this.element.autoplay = true;
                this.options.autoplay = true;
                this.toggleVideo(this.connection && this.sufficientConnection);
            }
            this.element.classList.remove('c-video--unitialized');
            this.initialized = true;
        } else {
            logger.warn('WARNING: You are using "Video" without its necessary dependency "Plyr".');
        }
    }

    /**
     * Compare the current and the preferred downlink.
     * @return {boolean} - If the current connection is better than the preferred one.
     */
    checkConnectivity() {
        /**
         * If no preferred connection type was passed we assume a great connection type (4G).
         * @type {string}
         */
        const preferredConnectionType = this.options.autoplayMinimumConnection ?? '4g';

        const currentDownlink = EffectiveConnectionTypeEnum[this.connection?.effectiveType];
        const minDownlink = EffectiveConnectionTypeEnum[preferredConnectionType];
        return currentDownlink >= minDownlink;
    }

    /**
     * Handle play-state of video depending on if the current page is visible.
     * Always pause the video when switching to another page tab.
     * Otherwise, resume playing the video if it has `autoplay` set when:
     *  - the user did not pause it when leaving the active tab,
     *  - and the user has not set a reduced motion setting,
     *  - and the connection is sufficient.
     */
    handleVisibilityChange() {
        if (document.hidden) {
            this.toggleVideo(false);
        } else if (
            (
                /**
                 * If `autoplay` was set but not restriction on connection bandwidth go ahead,
                 * otherwise we have an `autoplay` and need to check if the connectivity is still sufficient.
                 */
                (this.options.autoplay && !this.options.autoplayMinimumConnection) ||
                (this.options.autoplayMinimumConnection && this.sufficientConnection)
            ) &&
            !this.userForcedPause &&
            !this.prefersReducedMotion) {
            this.toggleVideo(true);
        }
    }

    /**
     * Toggle video playback state.
     * @param {boolean} shouldPlay - The current state of the video.
     */
    toggleVideo(shouldPlay) {
        if (shouldPlay) {
            this.play();
            this.videoControl?.classList.remove('is-paused');
            this.videoControl?.setAttribute('aria-label', 'Pause Video');
        } else {
            this.pause();
            this.videoControl?.classList.add('is-paused');
            this.videoControl?.setAttribute('aria-label', 'Play Video');
        }
    }

    /**
     * Play the video.
     */
    play() {
        this.plyr?.play();
    }

    /**
     * Pause the video.
     */
    pause() {
        this.plyr?.pause();
    }

    /**
     * Get the current play-state.
     * @returns {boolean}
     */
    get paused() {
        return this.plyr?.paused;
    }

}

VideoPlayer.register();

libs.Plyr = Plyr;
